/**
 * Юридические страницы «Умные цены» — add-price.ru/legal/:slug.
 *
 *   /legal/privacy — Политика конфиденциальности (152-ФЗ)
 *   /legal/terms   — Условия использования (пользовательское соглашение)
 *   /legal/offer   — Договор-оферта (платная подписка через биллинг МойСклад)
 *
 * Реквизиты правообладателя — единый источник в OPERATOR ниже.
 * Тексты — рабочая редакция на основе типовых шаблонов с упором на защиту
 * правообладателя (ПО предоставляется «как есть»). Перед публичным релизом
 * желательна вычитка профильным юристом.
 */
import { useParams, Link, Navigate } from 'react-router-dom';

// ─── Реквизиты правообладателя (единый источник) ─────────────────────
const OPERATOR = {
  fio: 'Индивидуальный предприниматель Ратушная Татьяна Александровна',
  short: 'ИП Ратушная Т. А.',
  ogrnip: '319774600581996',
  inn: '519055872906',
  address: '117216, г. Москва, ул. Грина, д. 1, корп. 1, кв. 93',
  email: 'support@e-ion.ru',
  site: 'provider.e-ion.ru',
  service: 'Умные цены',
};

// Редакция документов. Обновляйте при изменении текстов.
const EFFECTIVE_DATE = '31 мая 2026 г.';

// ─── Стилевые помощники ──────────────────────────────────────────────
const H2 = ({ children }: { children: React.ReactNode }): React.JSX.Element => (
  <h2 className="text-xl md:text-2xl font-semibold text-white mt-10 mb-3 tracking-tight">
    {children}
  </h2>
);
const P = ({ children }: { children: React.ReactNode }): React.JSX.Element => (
  <p className="text-ink-300 leading-relaxed mb-3">{children}</p>
);
const UL = ({ children }: { children: React.ReactNode }): React.JSX.Element => (
  <ul className="list-disc pl-6 space-y-1.5 text-ink-300 leading-relaxed mb-3">{children}</ul>
);
const B = ({ children }: { children: React.ReactNode }): React.JSX.Element => (
  <b className="text-ink-100 font-medium">{children}</b>
);
const Mail = (): React.JSX.Element => (
  <a className="text-accent" href={`mailto:${OPERATOR.email}`}>
    {OPERATOR.email}
  </a>
);

// ─── Блок реквизитов (общий низ всех документов) ─────────────────────
const Requisites = (): React.JSX.Element => (
  <>
    <H2>Реквизиты</H2>
    <UL>
      <li>{OPERATOR.fio}</li>
      <li>ОГРНИП: {OPERATOR.ogrnip}</li>
      <li>ИНН: {OPERATOR.inn}</li>
      <li>Адрес: {OPERATOR.address}</li>
      <li>
        E-mail для любых обращений (поддержка, договоры, оплата, ПДн): <Mail />
      </li>
      <li>Сайт: {OPERATOR.site}</li>
    </UL>
  </>
);

// ─── Контент документов ──────────────────────────────────────────────
interface LegalDoc {
  title: string;
  intro: string;
  body: React.ReactNode;
}

const PRIVACY: LegalDoc = {
  title: 'Политика конфиденциальности',
  intro:
    'Настоящая Политика описывает, какие данные обрабатывает приложение «Умные цены», с какой целью и как мы их защищаем. Обработка ведётся в соответствии с Федеральным законом № 152-ФЗ «О персональных данных».',
  body: (
    <>
      <H2>1. Оператор</H2>
      <P>
        Оператором обработки данных является {OPERATOR.fio} (далее — «Оператор», «мы»). Приложение «
        <B>{OPERATOR.service}</B>» (далее — «Сервис») предоставляет инструменты автоматического
        управления ценами в учётной системе МойСклад.
      </P>

      <H2>2. Какие данные мы обрабатываем</H2>
      <UL>
        <li>
          <B>Данные аккаунта МойСклад</B>: идентификатор аккаунта и токен доступа, выданный МойСклад
          при установке Сервиса. Токен необходим, чтобы читать и обновлять цены по вашему поручению.
        </li>
        <li>
          <B>Данные каталога</B>: сведения о товарах, ценах, остатках, справочниках — в объёме,
          необходимом для расчёта цен по заданным вами правилам.
        </li>
        <li>
          <B>Контактные данные</B>: адрес электронной почты и текст обращения, если вы пишете нам.
        </li>
        <li>
          <B>Технические данные</B>: обезличенная статистика использования интерфейса (через
          Яндекс.Метрику) — какие разделы открываются, какие действия выполняются. Эти данные не
          содержат сведений о ваших товарах и ценах.
        </li>
      </UL>
      <P>
        Сервис <B>не предназначен</B> для обработки специальных категорий персональных данных
        (раса, здоровье, политические взгляды и т. п.) и биометрии. Вы обязуетесь не загружать такие
        данные. За правомерность данных, размещённых вами в МойСклад, отвечаете вы.
      </P>

      <H2>3. Цели обработки</H2>
      <UL>
        <li>предоставление функций Сервиса: расчёт и запись цен по вашим правилам;</li>
        <li>формирование истории прогонов и резервных копий каталога;</li>
        <li>техническая поддержка и связь с вами;</li>
        <li>улучшение Сервиса на основе обезличенной аналитики.</li>
      </UL>

      <H2>4. Правовые основания</H2>
      <P>
        Обработка осуществляется на основании вашего согласия (выражается установкой и
        использованием Сервиса), для исполнения договора между вами и Оператором, а также для
        достижения иных законных целей, предусмотренных законодательством РФ.
      </P>

      <H2>5. Хранение и защита</H2>
      <UL>
        <li>данные размещаются на серверах, расположенных на территории Российской Федерации;</li>
        <li>
          токен доступа МойСклад хранится в зашифрованном виде (алгоритм AES-256-GCM); доступ к
          инфраструктуре ограничен и предоставляется по принципу минимальной необходимости;
        </li>
        <li>
          подробная история прогонов автоматически удаляется по истечении 10 дней; токен доступа
          удаляется при удалении Сервиса из вашего аккаунта.
        </li>
      </UL>
      <P>
        Мы принимаем разумные организационные и технические меры защиты. Вместе с тем ни один способ
        передачи и хранения данных не может быть гарантированно безопасным на 100 %; Оператор не
        несёт ответственности за последствия несанкционированного доступа, произошедшего не по вине
        Оператора.
      </P>

      <H2>6. Передача третьим лицам</H2>
      <P>
        Мы не продаём ваши данные. Для работы Сервиса привлекаются подрядчики (обработчики),
        действующие по нашему поручению и под нашим контролем: провайдер хостинга (РФ), сервис
        веб-аналитики Яндекс.Метрика (обезличенные данные), а также платформа МойСклад в рамках
        вашей учётной записи. Данные могут быть раскрыты по законному требованию уполномоченных
        органов.
      </P>

      <H2>7. Права субъекта данных</H2>
      <P>
        Вы вправе запросить сведения об обработке ваших данных, их уточнение, блокирование или
        удаление, а также отозвать согласие, направив обращение на <Mail />. Отзыв согласия может
        повлечь невозможность предоставления Сервиса.
      </P>

      <H2>8. Cookies и аналитика</H2>
      <P>
        Сайт и приложение могут использовать cookies и аналитические скрипты (Яндекс.Метрика) для
        корректной работы интерфейса и обезличенной статистики. Вы можете ограничить cookies в
        настройках браузера.
      </P>

      <H2>9. Изменения Политики</H2>
      <P>
        Мы можем обновлять настоящую Политику. Актуальная редакция всегда доступна по адресу{' '}
        {OPERATOR.site}/legal/privacy. Продолжая пользоваться Сервисом после изменений, вы
        принимаете обновлённую редакцию.
      </P>

      <Requisites />
    </>
  ),
};

const TERMS: LegalDoc = {
  title: 'Условия использования',
  intro:
    'Настоящие Условия (пользовательское соглашение) регулируют использование приложения «Умные цены». Устанавливая и используя Сервис, вы полностью и безоговорочно соглашаетесь с этими Условиями. Если вы не согласны — не используйте Сервис.',
  body: (
    <>
      <H2>1. Предмет</H2>
      <P>
        «<B>{OPERATOR.service}</B>» — программное обеспечение (приложение для платформы МойСклад),
        которое по заданным вами правилам рассчитывает цены и обновляет их в вашем аккаунте
        МойСклад: расчёт по формулам и сегментам, запуск вручную и по расписанию, ведение истории и
        резервных копий каталога.
      </P>

      <H2>2. Порядок использования</H2>
      <UL>
        <li>Сервис устанавливается из каталога приложений МойСклад в ваш аккаунт;</li>
        <li>
          для работы Сервис использует доступ к вашему аккаунту МойСклад, предоставленный при
          установке;
        </li>
        <li>вы самостоятельно настраиваете правила ценообразования и запускаете прогоны.</li>
      </UL>

      <H2>3. Ответственность пользователя</H2>
      <P>
        Вы используете Сервис на свой риск и <B>самостоятельно отвечаете</B> за корректность
        настроенных правил, формул и условий, а также за итоговые цены, которые Сервис записывает в
        ваш каталог по вашему поручению. Перед запуском в боевом режиме мы настоятельно рекомендуем
        использовать <B>проверочный режим</B> (расчёт без записи) и сохранять <B>резервную копию</B>{' '}
        каталога — эти инструменты встроены в Сервис. Вы обязуетесь использовать Сервис правомерно и
        не нарушать прав третьих лиц.
      </P>

      <H2>4. Гарантии и их ограничение</H2>
      <P>
        Сервис предоставляется по принципу «<B>как есть</B>» (as is) и «как доступно» (as available).
        Оператор не гарантирует, что Сервис будет работать бесперебойно, без ошибок и задержек, что
        результат расчёта подойдёт под ваши конкретные бизнес-цели, и не даёт иных явных или
        подразумеваемых гарантий в пределах, допускаемых законодательством РФ. Работа Сервиса
        зависит от сторонних систем (МойСклад, провайдеры связи и хостинга), за которые Оператор не
        отвечает.
      </P>

      <H2>5. Ограничение ответственности</H2>
      <UL>
        <li>
          Оператор не несёт ответственности за упущенную выгоду, недополученные доходы, потерю
          данных, репутационный вред и любые косвенные или случайные убытки, возникшие в связи с
          использованием или невозможностью использования Сервиса;
        </li>
        <li>
          Оператор не отвечает за финансовые последствия установленных вами цен (в том числе за
          ошибочные, заниженные или завышенные цены, возникшие из ваших правил или из данных в
          МойСклад);
        </li>
        <li>
          совокупная ответственность Оператора в любом случае ограничена суммой, фактически
          уплаченной вами за подписку за период (но не более чем за 1 месяц), в котором возникло
          основание для претензии; для бесплатного использования — ограничена нулём.
        </li>
      </UL>

      <H2>6. Допустимое использование</H2>
      <P>
        Запрещается: декомпилировать, дизассемблировать и иным образом пытаться извлечь исходный код
        Сервиса; создавать чрезмерную нагрузку, обходить ограничения и средства защиты;
        использовать Сервис для противоправных целей. Оператор вправе ограничить или прекратить
        доступ при нарушении этих условий.
      </P>

      <H2>7. Интеллектуальная собственность</H2>
      <P>
        Исключительные права на Сервис, его интерфейс, исходный код, дизайн и материалы принадлежат
        Оператору. Использование Сервиса не передаёт вам каких-либо прав на него, кроме права
        использования по назначению в период действия доступа/подписки.
      </P>

      <H2>8. Изменение и прекращение</H2>
      <P>
        Оператор вправе развивать, изменять, временно приостанавливать (в том числе для технических
        работ) и прекращать Сервис или отдельные функции, а также обновлять настоящие Условия. Вы
        можете прекратить использование в любой момент, удалив Сервис из аккаунта МойСклад.
      </P>

      <H2>9. Применимое право и споры</H2>
      <P>
        К настоящим Условиям применяется законодательство Российской Федерации. Споры разрешаются
        путём переговоров, а при недостижении согласия — в суде по месту нахождения Оператора, с
        соблюдением обязательного претензионного порядка (срок ответа на претензию — 30 дней).
      </P>

      <Requisites />
    </>
  ),
};

const OFFER: LegalDoc = {
  title: 'Договор-оферта',
  intro:
    'Настоящий документ является публичной офертой на предоставление доступа к приложению «Умные цены». Акцептом оферты считается установка и (или) использование Сервиса, а равно оплата подписки.',
  body: (
    <>
      <H2>1. Термины</H2>
      <UL>
        <li>
          <B>Оператор</B> — {OPERATOR.fio}.
        </li>
        <li>
          <B>Сервис</B> — приложение «{OPERATOR.service}» в каталоге МойСклад.
        </li>
        <li>
          <B>Пользователь</B> — лицо, установившее и использующее Сервис в своём аккаунте МойСклад.
        </li>
        <li>
          <B>Платформа</B> — сервис МойСклад, через который осуществляются установка, тарификация и
          приём оплаты.
        </li>
      </UL>

      <H2>2. Предмет договора</H2>
      <P>
        Оператор предоставляет Пользователю право использования Сервиса на условиях подписки, а
        Пользователь оплачивает подписку в соответствии с выбранным тарифом. Право использования —
        простое (неисключительное), без права передачи третьим лицам.
      </P>

      <H2>3. Заключение договора</H2>
      <P>
        Договор считается заключённым с момента акцепта — установки Сервиса из каталога МойСклад и
        (или) начала его использования. Если предусмотрен <B>бесплатный пробный период</B>, договор
        действует на условиях пробного периода до его окончания.
      </P>

      <H2>4. Стоимость и порядок оплаты</H2>
      <UL>
        <li>
          стоимость и состав тарифов указываются в карточке Сервиса в каталоге МойСклад; тарифы
          могут включать бесплатный пробный период;
        </li>
        <li>
          приём оплаты и тарификация осуществляются <B>средствами Платформы МойСклад</B> в
          соответствии с её правилами; Оператор не принимает оплату напрямую;
        </li>
        <li>
          подписка продлевается и прекращается в порядке, предусмотренном Платформой; при отсутствии
          действующей подписки доступ к платным функциям Сервиса приостанавливается.
        </li>
      </UL>

      <H2>5. Права и обязанности сторон</H2>
      <UL>
        <li>Оператор обязуется обеспечивать работу Сервиса в соответствии с его описанием;</li>
        <li>
          Пользователь обязуется использовать Сервис правомерно и самостоятельно отвечает за
          настроенные им правила и итоговые цены;
        </li>
        <li>
          Оператор вправе изменять и развивать функции Сервиса, проводить технические работы,
          уведомляя о существенных изменениях доступными способами.
        </li>
      </UL>

      <H2>6. Гарантии, ответственность, возврат</H2>
      <P>
        Сервис предоставляется «как есть». Ограничения гарантий и ответственности применяются в том
        же объёме, что и в Условиях использования (раздел «Гарантии» и «Ограничение
        ответственности»). Вопросы возврата средств за подписку регулируются правилами Платформы
        МойСклад, через которую произведена оплата; по спорным ситуациям обращайтесь на <Mail />.
      </P>

      <H2>7. Форс-мажор</H2>
      <P>
        Стороны освобождаются от ответственности за неисполнение обязательств, вызванное
        обстоятельствами непреодолимой силы (в том числе сбои сторонних сервисов, изменения в работе
        Платформы, ограничения со стороны органов власти).
      </P>

      <H2>8. Срок действия</H2>
      <P>
        Оферта действует с даты её публикации и до отзыва Оператором. Действующая редакция размещена
        по адресу {OPERATOR.site}/legal/offer. К отношениям сторон также применяется Политика
        конфиденциальности и Условия использования.
      </P>

      <Requisites />
    </>
  ),
};

const DOCS: Record<string, LegalDoc> = {
  privacy: PRIVACY,
  terms: TERMS,
  offer: OFFER,
};

export const LegalPage = (): React.JSX.Element => {
  const { slug } = useParams<{ slug: string }>();
  const doc = slug ? DOCS[slug] : undefined;

  if (!doc) return <Navigate to="/" replace />;

  return (
    <section className="container py-12 md:py-16">
      <div className="max-w-3xl">
        <nav className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-400 mb-6">
          <Link to="/legal/privacy" className="hover:text-white transition-colors">
            Конфиденциальность
          </Link>
          <Link to="/legal/terms" className="hover:text-white transition-colors">
            Условия использования
          </Link>
          <Link to="/legal/offer" className="hover:text-white transition-colors">
            Договор-оферта
          </Link>
        </nav>

        <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-white">{doc.title}</h1>
        <p className="mt-3 text-ink-300 leading-relaxed">{doc.intro}</p>

        <div className="mt-8">{doc.body}</div>

        <p className="mt-12 pt-6 border-t border-ink-800 text-xs text-ink-500">
          Редакция от {EFFECTIVE_DATE}. {OPERATOR.short}.
        </p>
      </div>
    </section>
  );
};
